// ── THE SALES PAGE SCROLL HEAT MAP ───────────────────────────────────────────
//
// Opened from the sales card's "How far people scroll" heading, in the same
// full-width panel the video "View drop-off" uses (DropOffModal's Modal).
//
// ONE SOURCE: GET /api/admin/sales-heatmap, sent exactly the date range and paid
// flag the sales card itself uses — so "Landed" is the card's unique
// visitors and the 25/50/75/90 column is the card's own scroll chart, to the
// person. The finer data (every 10% of the page, 21 named sections) exists
// only from the moment the sales page began recording it; before that the
// panel shows the checkpoints alone and says why.
//
// NOTHING IS DRAWN THAT ISN'T THERE. No visitors → one plain line, never a
// ladder of 0% bars. Colour only encodes the share reached: strong warm at a
// high share, fading to pale at a low one.
//
// THE PICTURE VIEW (since 2026-09-30, PictureHeatmap.jsx): when the range has
// visitors recorded with scroll_max (the response's picture.pictureMode), the
// panel opens on a Clarity-style colour wash over a real picture of the page —
// Mobile by default, Desktop by toggle — and the section drop-offs below
// follow that toggle. Otherwise the ladder view below is shown exactly as
// before, with one line saying when the picture view has data from.

import { useState, useEffect, useRef, useCallback } from 'react';
import { C } from './adminShared';
import { Modal } from './DropOffModal';
import { getAdminSalesHeatmap } from '../../utils/api';
import { PictureHeatmap, PICTURE_TO_CARD_VIEW } from './PictureHeatmap';

const PALE = [251, 241, 234];      // #FBF1EA
const TERRACOTTA = [200, 105, 58]; // #C8693A
const RUST = [184, 52, 24];        // #B83418
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;
const shade = (pct) => {
  if (pct == null) return '#F6EFE9';
  const t = Math.max(0, Math.min(1, pct / 100));
  return rgb(t < 0.5 ? mix(PALE, TERRACOTTA, t / 0.5) : mix(TERRACOTTA, RUST, (t - 0.5) / 0.5));
};
const ink = (pct) => (pct != null && pct >= 45 ? '#fff' : C.heading);
const fmtPct = (p) => (p == null ? '—' : `${Math.round(p)}%`);
const fmtWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtWhenShort = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;

const DEVICES = [
  { id: 'all', label: 'All devices' },
  { id: 'mobile', label: 'Mobile' },
  { id: 'other', label: 'Other (desktop / tablet)' },
];

// ── The page drop-off ladder, with the 25/50/75/90 column beside it ─────────
const ROW = 30, GAP = 18;
const Ladder = ({ rows, checkpoints }) => {
  // rows: [{ key, label, count, pct, drop }] top to bottom; row k sits at k*(ROW+GAP).
  // checkpoints: [{ at, count, pct, pos }] — pos in row units (25 → 2.5).
  const height = rows.length * ROW + (rows.length - 1) * GAP;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <div style={{ position: 'relative', flex: 1, minWidth: 0, height }}>
        {rows.map((r, k) => (
          <div key={r.key}>
            <div style={{
              position: 'absolute', left: 0, right: 0, top: k * (ROW + GAP), height: ROW,
              background: shade(r.pct), color: ink(r.pct), borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px',
              fontSize: 11.5, border: `1px solid ${C.border}`,
            }}>
              <span style={{ fontWeight: 600 }}>{r.label}</span>
              <span><strong>{fmtPct(r.pct)}</strong><span style={{ opacity: 0.8 }}> · {r.count}</span></span>
            </div>
            {k < rows.length - 1 && (
              <div style={{
                position: 'absolute', left: 10, top: k * (ROW + GAP) + ROW, height: GAP,
                display: 'flex', alignItems: 'center', fontSize: 9.5, color: C.accent,
              }}>
                {rows[k + 1].drop > 0
                  ? <>▼ {people(rows[k + 1].drop)} fewer{r.pct != null && rows[k + 1].pct != null ? ` (−${Math.round(r.pct - rows[k + 1].pct)} pts)` : ''}</>
                  : <span style={{ opacity: 0.6 }}>no one lost</span>}
              </div>
            )}
          </div>
        ))}
      </div>
      {checkpoints && (
        <div style={{ position: 'relative', width: 92, flexShrink: 0, height }}>
          {checkpoints.map(c => (
            <div key={c.at} title={`Reached at least ${c.at}% (the page's own 25/50/75/90 checkpoints)`} style={{
              position: 'absolute', left: 0, right: 0, top: c.pos * (ROW + GAP), height: ROW,
              background: shade(c.pct), color: ink(c.pct), borderRadius: 6, border: `1px dashed ${C.accent}`,
              display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 7px', fontSize: 9.5, lineHeight: 1.15,
            }}>
              <span style={{ fontWeight: 700 }}>{c.at}% · {fmtPct(c.pct)}</span>
              <span style={{ opacity: 0.85 }}>{people(c.count)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Sections, top to bottom ────────────────────────────────────────────────
const SectionsList = ({ sections }) => (
  <div>
    {sections.map((s, i) => (
      <div key={s.id}>
        {i > 0 && (
          <div style={{ fontSize: 9.5, color: s.largestDrop ? '#B83418' : C.accent, fontWeight: s.largestDrop ? 700 : 400, margin: '2px 0 2px 10px' }}>
            {s.drop > 0 ? <>▼ {people(s.drop)} fewer{s.largestDrop ? ' — largest drop' : ''}</> : <span style={{ opacity: 0.6 }}>no one lost</span>}
          </div>
        )}
        <div style={{
          background: shade(s.pct), color: ink(s.pct), borderRadius: 6, minHeight: 26,
          border: s.largestDrop ? '2px solid #B83418' : `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '3px 10px', fontSize: 11.5,
        }}>
          <span style={{ fontWeight: 600, minWidth: 0 }}>{s.order}. {s.name}</span>
          <span style={{ whiteSpace: 'nowrap' }}><strong>{fmtPct(s.pct)}</strong><span style={{ opacity: 0.8 }}> · {s.count}</span></span>
        </div>
      </div>
    ))}
  </div>
);

// ── Visitors ────────────────────────────────────────────────────────────────
const Tick = ({ on }) => <span style={{ color: on ? C.green : C.border, fontWeight: 700 }}>{on ? '✓' : '–'}</span>;
const SectionDots = ({ seen, list }) => (
  <span title={list.filter(s => seen.includes(s.id)).map(s => s.name).join(', ') || 'none'} style={{ display: 'inline-flex', gap: 1, verticalAlign: 'middle' }}>
    {list.map(s => (
      <span key={s.id} style={{ width: 4, height: 10, borderRadius: 1, background: seen.includes(s.id) ? '#C8693A' : '#EFE3D8' }} />
    ))}
  </span>
);
const VisitorsTable = ({ visitors, total, list }) => (
  <div>
    <div style={{ fontSize: 11, color: C.accent, marginBottom: 6 }}>
      {total > visitors.length ? `Showing the newest ${visitors.length} of ${total} visitors with finer scroll data` : `${total} ${total === 1 ? 'visitor' : 'visitors'} with finer scroll data`} — newest first.
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 760 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${C.border}`, textAlign: 'left', color: C.heading }}>
            {['Arrived', 'Source', 'Device', 'How far', 'Sections seen', 'CTA', 'Checkout', 'Bought', 'Who'].map(h => (
              <th key={h} style={{ padding: '6px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visitors.map((v, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtWhenShort(v.firstArrival)}</td>
              <td style={{ padding: '6px 8px' }}>{v.source?.label || '—'}</td>
              <td style={{ padding: '6px 8px' }}>{v.device === 'mobile' ? 'Mobile' : v.device === 'other' ? 'Other' : 'Unknown'}</td>
              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                {v.furthestPct != null ? <><strong>{v.furthestPct}%</strong> furthest point · </> : null}
                {v.furthestBand ? `${v.furthestBand}% of page` : 'under 10%'}
                {v.furthestCheckpoint ? <span style={{ color: C.accent }}> · checkpoint {v.furthestCheckpoint}%</span> : null}
              </td>
              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                <SectionDots seen={v.sections} list={list} /> <span style={{ color: C.accent }}>{v.sections.length}/{list.length}</span>
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'center' }}><Tick on={v.clickedCta} /></td>
              <td style={{ padding: '6px 8px', textAlign: 'center' }}><Tick on={v.openedCheckout} /></td>
              <td style={{ padding: '6px 8px', textAlign: 'center' }}><Tick on={v.purchased} /></td>
              <td style={{ padding: '6px 8px' }}>
                {v.lead ? <>{v.lead.firstName || ''}{v.lead.email ? <span style={{ color: C.accent }}> {v.lead.email}</span> : null}</> : <span style={{ color: C.border }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── The panel body ──────────────────────────────────────────────────────────
const HeatMapBody = ({ from, to, paidOnly, extra = {}, rangeText }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [device, setDevice] = useState('all');
  const [pictureView, setPictureView] = useState('mobile');
  useEffect(() => {
    let on = true;
    getAdminSalesHeatmap(from, to, paidOnly, extra)
      .then(r => { if (on) setState({ loading: false, error: null, data: r.data }); })
      .catch(e => { if (on) setState({ loading: false, error: e?.response?.data?.error || e.message || 'Could not load', data: null }); });
    return () => { on = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.loading) return <div style={{ fontSize: 12, color: C.accent, padding: '12px 0' }}>Loading…</div>;
  if (state.error) return <div style={{ fontSize: 12, color: C.red, padding: '12px 0' }}>✕ Couldn’t load the heat map: {state.error}</div>;
  const d = state.data;
  const pic = d.picture && d.picture.pictureMode ? d.picture : null;
  const v = d.views[device];
  const deviceWord = DEVICES.find(x => x.id === device).label.toLowerCase();
  const rec = d.recordingSince;

  if (pic) {
    const cardView = PICTURE_TO_CARD_VIEW[pictureView];
    const sv = d.views[cardView];
    const noun = pictureView === 'mobile' ? 'phone visitors' : 'desktop visitors';
    return (
      <div style={{ color: C.text }}>
        <div style={{ fontSize: 11, color: C.accent, marginBottom: 12, lineHeight: 1.5 }}>
          Sales page visitors · {rangeText} · {paidOnly ? 'paid traffic only (Meta ads)' : 'all traffic'}.
          {' '}Bots, Ben’s own devices and test visits are left out, exactly as on the sales card.
        </div>
        <PictureHeatmap picture={pic} paidOnly={paidOnly} view={pictureView} setView={setPictureView} />
        <div style={{ fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
          Sections seen, top to bottom — {noun}{sv.sections ? `, of ${people(sv.sectionsBase)}${d.mode === 'tenths' ? ' who landed' : ' who landed since recording began'}` : ''}
        </div>
        <div style={{ maxWidth: 560, marginBottom: 18 }}>
          {sv.sections && sv.sectionsBase > 0
            ? <SectionsList sections={sv.sections} />
            : <div style={{ fontSize: 11.5, color: C.accent }}>No section data for {noun} in this range.</div>}
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 0 6px' }}>
          Visitors
        </div>
        {d.visitorsTotal > 0
          ? <VisitorsTable visitors={d.visitors} total={d.visitorsTotal} list={d.sectionsList} />
          : <div style={{ fontSize: 11.5, color: C.accent }}>No visitor in this range has finer scroll data yet.</div>}
      </div>
    );
  }

  const checkpointsAsRows = [
    { key: 'landed', label: 'Landed', count: v.landed, pct: v.landed > 0 ? 100 : null, drop: null },
    ...v.checkpoints.map((c, i, arr) => ({
      key: `c${c.at}`, label: `Reached ${c.at}%`, count: c.count, pct: c.pct,
      drop: (i === 0 ? v.landed : arr[i - 1].count) - c.count,
    })),
  ];
  const tenthRows = v.tenths ? [
    { key: 'landed', label: 'Landed', count: v.landed, pct: v.landed > 0 ? 100 : null, drop: null },
    ...v.tenths.map(t => ({ key: `b${t.band}`, label: `${t.band}% of the page`, count: t.count, pct: t.pct, drop: t.drop })),
  ] : null;
  const checkpointCol = v.checkpoints.map(c => ({ ...c, pos: c.at / 10 }));

  return (
    <div style={{ color: C.text }}>
      {/* a) what this is, and exactly which visitors */}
      <div style={{ fontSize: 12, color: C.heading, marginBottom: 4 }}>
        {rec
          ? <>Finer scroll data (every 10% of the page and each section) recording since <strong>{fmtWhen(rec)}</strong>.</>
          : <><strong>Not recording yet</strong> — no real visitor’s finer scroll data has arrived. The 25/50/75/90 checkpoints below go back to launch.</>}
      </div>
      <div style={{ fontSize: 11, color: C.accent, marginBottom: 12, lineHeight: 1.5 }}>
        Sales page visitors · {rangeText} · {paidOnly ? 'paid traffic only (Meta ads)' : 'all traffic'} · {deviceWord}.
        {' '}Bots, Ben’s own devices and test visits are left out, exactly as on the sales card.
      </div>
      {d.picture && (
        <div data-testid="picture-unavailable" style={{ fontSize: 11, color: C.text, background: C.blueBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px', marginBottom: 12, lineHeight: 1.5 }}>
          {d.picture.scrollMaxSince
            ? <>Picture view has data from {fmtWhen(d.picture.scrollMaxSince)} — pick a range from then to see it.</>
            : <>Picture view will appear once real visitors have been recorded with the sales page’s exact scroll point (recording since 30 Sep 2026).</>}
        </div>
      )}

      {/* b) device */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {DEVICES.map(x => (
          <button key={x.id} type="button" onClick={() => setDevice(x.id)} aria-pressed={device === x.id} style={{
            fontSize: 11, fontWeight: device === x.id ? 800 : 600, padding: '5px 11px', borderRadius: 999, cursor: 'pointer',
            border: `1px solid ${device === x.id ? C.heading : C.border}`, background: device === x.id ? C.heading : 'transparent',
            color: device === x.id ? '#fff' : C.text,
          }}>{x.label}</button>
        ))}
        {device === 'all' && d.unknownDevice > 0 && (
          <span style={{ fontSize: 10, color: C.accent, alignSelf: 'center' }}>
            {people(d.unknownDevice)} whose device couldn’t be told {d.unknownDevice === 1 ? 'is' : 'are'} counted in All only.
          </span>
        )}
      </div>

      {v.landed === 0 ? (
        <div style={{ fontSize: 12, color: C.accent, padding: '8px 0 16px' }}>
          No {device === 'all' ? '' : `${DEVICES.find(x => x.id === device).label.toLowerCase()} `}sales page visitors in this range — nothing to show.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 18 }}>
          {/* c) page drop-off + checkpoints */}
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              How far down the page — of {people(v.landed)} who landed
            </div>
            {d.mode === 'tenths' && tenthRows ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 9, color: C.accent, marginBottom: 3 }}>
                  <span style={{ width: 92 }}>25/50/75/90</span>
                </div>
                <Ladder rows={tenthRows} checkpoints={checkpointCol} />
                <div style={{ fontSize: 9.5, color: C.accent, fontStyle: 'italic', marginTop: 8, lineHeight: 1.5 }}>
                  Each step is the share who scrolled to at least that point — the same measure as the page’s
                  25/50/75/90 checkpoints (dashed, right), so 50% and 90% line up with them.
                </div>
              </>
            ) : (
              <>
                <Ladder rows={checkpointsAsRows} />
                <div style={{ fontSize: 10.5, color: C.text, background: C.amberBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', marginTop: 10, lineHeight: 1.5 }}>
                  {rec
                    ? <>Finer 10% steps recorded from {fmtWhen(rec)} — pick a range starting on or after that date to see them (a range has to start after that moment, so in practice from the following day).</>
                    : <>Finer 10% steps appear once real visitors have been recorded; until then these are the page’s 25/50/75/90 checkpoints.</>}
                </div>
              </>
            )}
          </div>

          {/* d) sections */}
          <div style={{ flex: '1 1 300px', minWidth: 260 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Sections seen, top to bottom{v.sections ? ` — of ${people(v.sectionsBase)}${d.mode === 'tenths' ? ' who landed' : ' who landed since recording began'}` : ''}
            </div>
            {v.sections && v.sectionsBase > 0 ? (
              <SectionsList sections={v.sections} />
            ) : (
              <div style={{ fontSize: 11.5, color: C.accent, lineHeight: 1.5 }}>
                {rec ? <>Section data recorded from {fmtWhen(rec)}{v.sections ? '' : ' — none in this range'}.</> : <>Section data will appear once real visitors have been recorded.</>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* e) visitors */}
      <div style={{ fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 0 6px' }}>
        Visitors
      </div>
      {d.visitorsTotal > 0
        ? <VisitorsTable visitors={d.visitors} total={d.visitorsTotal} list={d.sectionsList} />
        : <div style={{ fontSize: 11.5, color: C.accent }}>No visitor in this range has finer scroll data yet.</div>}
    </div>
  );
};

// ── PUBLIC: the quiet link plus the panel it opens ─────────────────────────
export const HeatMapLink = ({ from, to, paidOnly, extra = {}, rangeText }) => {
  const [open, setOpen] = useState(false);
  const linkRef = useRef(null);
  const close = useCallback(() => { setOpen(false); linkRef.current?.focus(); }, []);
  return (
    <>
      <button ref={linkRef} onClick={() => setOpen(true)} style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        font: 'inherit', fontSize: 10, color: C.accent,
        textDecoration: 'underline', textUnderlineOffset: 2, flexShrink: 0,
      }}>View heat map</button>
      {open && (
        <Modal title="Sales page — how far people get" subtitle="Share of the page’s unique visitors who reached each point, and each section" onClose={close}>
          <HeatMapBody from={from} to={to} paidOnly={paidOnly} extra={extra} rangeText={rangeText} />
        </Modal>
      )}
    </>
  );
};
